import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { DataDomainForm } from '../../components/information';
import { createDataDomain, ApiError, type CreateDataDomainInput } from '../../lib/apiClient';
import type { CreateDataDomainFormData } from '../../components/forms/validation';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';
import './new.css';

export const Route = createFileRoute('/information/new')({
  component: CreateDataDomainPage,
});

export { CreateDataDomainPage };

function CreateDataDomainPage(): JSX.Element {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  const handleSubmit = async (data: CreateDataDomainFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const input: CreateDataDomainInput = {
        name: data.name,
        /* c8 ignore next */
        description: data.description || undefined,
        domainType: data.domainType,
        dataStewardId: data.dataStewardId,
        owningBusinessUnitId: data.owningBusinessUnitId,
        /* c8 ignore next */
        technicalOwnerId: data.technicalOwnerId || undefined,
        defaultClassification: data.defaultClassification,
        /* c8 ignore next */
        containsPII: data.containsPII ?? false,
        /* c8 ignore next */
        containsPCI: data.containsPCI ?? false,
        /* c8 ignore next */
        containsPHI: data.containsPHI ?? false,
        /* c8 ignore next */
        lifecycleStatus: data.lifecycleStatus || undefined,
        criticalityLevel: data.criticalityLevel,
        /* c8 ignore next */
        notes: data.notes || undefined,
        /* c8 ignore next */
        documentationUrl: data.documentationUrl || undefined,
      };
      const domain = await createDataDomain(input);
      void navigate({
        to: '/information/$dataDomainId',
        params: { dataDomainId: domain.entityId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/information', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="create-data-domain-page">
      <div className="breadcrumb">
        <Link to="/information" search={{ page: 1, pageSize: 25 }} className="breadcrumb-link">
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Data Domains</span>
        </Link>
      </div>

      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Data Domain</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new data domain to the EA catalog
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <DataDomainForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
